"use client";

import { api } from "@agroproj/backend/convex/_generated/api";
import { usePaginatedQuery, useQuery as useCachedQuery } from "convex-helpers/react/cache/hooks";
import { useQuery } from "convex/react";
import type { OptionalRestArgsOrSkip, PaginatedQueryArgs, PaginatedQueryItem, PaginatedQueryReference, UsePaginatedQueryReturnType } from "convex/react";
import { getFunctionName } from "convex/server";
import type { FunctionReference, FunctionReturnType } from "convex/server";
import { useRef } from "react";

/** Scope retained results to both the authenticated user and the active organization. */
export function useQueryScope() {
  const membership = useQuery(api.check_membership.hasOrganization);
  return membership?.isAuthenticated && membership.userId && membership.activeOrganizationId ? `${membership.userId}:${membership.activeOrganizationId}` : undefined;
}

/** Keep list results during argument changes, and cache live subscriptions for revisits. */
export function useStableQuery<Query extends FunctionReference<"query">>(query: Query, options: {
  retentionKey?: string;
}, ...args: OptionalRestArgsOrSkip<Query>): {
  data: FunctionReturnType<Query> | undefined;
  isStale: boolean;
} {
  const organization = useQueryScope();
  const queryArgs: OptionalRestArgsOrSkip<Query> = organization ? args : ["skip"];
  const result = useCachedQuery(query, ...queryArgs);
  const queryName = getFunctionName(query);
  const previous = useRef<{
    organization: string | null | undefined;
    queryName: string;
    retentionKey?: string;
    data: FunctionReturnType<Query> | undefined;
  }>({
    organization,
    queryName,
    retentionKey: options.retentionKey,
    data: undefined
  });
  if (previous.current.organization !== organization || previous.current.queryName !== queryName || previous.current.retentionKey !== options.retentionKey) {
    previous.current = {
      organization,
      queryName,
      retentionKey: options.retentionKey,
      data: undefined
    };
  }
  if (args[0] === "skip" || !organization) {
    previous.current.data = undefined;
    return {
      data: undefined,
      isStale: false
    };
  }
  // A ref avoids render-phase state updates when queries return fresh arrays.
  if (result !== undefined) previous.current.data = result;
  return {
    data: previous.current.data,
    isStale: result === undefined && previous.current.data !== undefined
  };
}

/**
 * Organization route changes unmount these hooks through EnforceOrganization,
 * resetting their cursors. Cached subscriptions make revisits immediate; retained
 * rows cover argument changes. The convex-helpers patch gives cursor recovery a
 * fresh pagination ID so it cannot repeatedly read the same cached error.
 */
export function useStablePaginatedQuery<Query extends PaginatedQueryReference>(query: Query, args: PaginatedQueryArgs<Query> | "skip", options: {
  initialNumItems: number;
}): UsePaginatedQueryReturnType<Query> & {
  isStale: boolean;
} {
  const organization = useQueryScope();
  const result = usePaginatedQuery(query, organization ? args : "skip", options);
  const queryName = getFunctionName(query);
  const previous = useRef<{
    organization: string | null | undefined;
    queryName: string;
    rows: PaginatedQueryItem<Query>[] | undefined;
  }>({
    organization,
    queryName,
    rows: undefined
  });
  if (previous.current.organization !== organization || previous.current.queryName !== queryName) {
    previous.current = {
      organization,
      queryName,
      rows: undefined
    };
  }
  if (args === "skip" || !organization) {
    previous.current.rows = undefined;
    return {
      ...result,
      results: [],
      isStale: false
    };
  }
  // Splitting a native range can temporarily hide all rows while LoadingMore.
  const isPending = result.status === "LoadingFirstPage" || result.status === "LoadingMore" && result.results.length === 0;
  if (!isPending) previous.current.rows = result.results;
  const kept = isPending ? previous.current.rows : undefined;
  return {
    ...result,
    results: kept ?? result.results,
    isStale: kept !== undefined && kept.length > 0
  };
}